import React from "react";
import { Link, useLocation } from "react-router-dom";

/*
 * Stepper for the checkout flow. Driven by URL via useLocation rather than
 * the legacy step1/step2/... props (those are still accepted but ignored,
 * so existing callers don't break). The progression is:
 *   /login or /register  → step 0 (Identificación)
 *   /shipping            → step 1 (Envío)
 *   /payment             → between (1.5) — Pago is not in the breadcrumb
 *                          but Envío shows as "done" while we're there
 *   /placeorder          → step 2 (Pedido)
 */
const CheckoutSteps = () => {
  const { pathname } = useLocation();

  let currentStep;
  if (pathname.startsWith("/placeorder")) currentStep = 2;
  else if (pathname.startsWith("/payment")) currentStep = 1.5;
  else if (pathname.startsWith("/shipping")) currentStep = 1;
  else currentStep = 0;

  const steps = [
    { label: "Identificación", to: "/login" },
    { label: "Envío", to: "/shipping" },
    { label: "Pedido", to: "/placeorder" },
  ];

  const isDone = (i) => i < currentStep;
  const isCurrent = (i) => Math.abs(i - currentStep) < 0.001;

  return (
    <>
      <style>{`
        .hadacy-stepper {
          --gold: #b58c2c;
          --gold-light: #d4a949;
          --dark: #2a1f0c;
          --cream: #f5efe1;
          --muted: #8b7458;
          --hairline: rgba(43, 31, 12, 0.18);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 24px 0 36px;
          gap: 0;
          flex-wrap: nowrap;
        }
        .hadacy-stepper__step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          font-family: 'Jost', sans-serif;
          flex: 0 0 auto;
          min-width: 110px;
          transition: opacity 0.2s ease;
        }
        .hadacy-stepper__step--done:hover {
          opacity: 0.85;
        }
        .hadacy-stepper__circle {
          width: 34px;
          height: 34px;
          border-radius: 999px;
          border: 1px solid var(--hairline);
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 500;
          color: var(--muted);
          transition: all 0.2s ease;
        }
        .hadacy-stepper__step--current .hadacy-stepper__circle {
          background: var(--dark);
          border-color: var(--dark);
          color: var(--cream);
        }
        .hadacy-stepper__step--done .hadacy-stepper__circle {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--cream);
        }
        .hadacy-stepper__label {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          text-align: center;
          line-height: 1.2;
        }
        .hadacy-stepper__step--current .hadacy-stepper__label,
        .hadacy-stepper__step--done .hadacy-stepper__label {
          color: var(--dark);
          font-weight: 600;
        }
        .hadacy-stepper__line {
          flex: 0 0 80px;
          height: 1px;
          background: var(--hairline);
          align-self: flex-start;
          margin-top: 17px;
        }
        .hadacy-stepper__line--done {
          background: var(--gold);
        }
        @media (max-width: 480px) {
          .hadacy-stepper { padding: 18px 0 26px; }
          .hadacy-stepper__line { flex: 0 0 28px; }
          .hadacy-stepper__step { min-width: 78px; }
          .hadacy-stepper__label { font-size: 10px; letter-spacing: 0.12em; }
        }
      `}</style>
      <nav className="hadacy-stepper" aria-label="Progreso de la compra">
        {steps.map((s, i) => {
          const done = isDone(i);
          const current = isCurrent(i);
          const cls = [
            "hadacy-stepper__step",
            done ? "hadacy-stepper__step--done" : "",
            current ? "hadacy-stepper__step--current" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const inner = (
            <>
              <span className="hadacy-stepper__circle" aria-hidden="true">
                {done ? <i className="fas fa-check"></i> : i + 1}
              </span>
              <span className="hadacy-stepper__label">{s.label}</span>
            </>
          );

          return (
            <React.Fragment key={s.label}>
              {done ? (
                <Link to={s.to} className={cls}>
                  {inner}
                </Link>
              ) : (
                <span className={cls} aria-current={current ? "step" : undefined}>
                  {inner}
                </span>
              )}
              {i < steps.length - 1 && (
                <span
                  className={
                    "hadacy-stepper__line" +
                    (done ? " hadacy-stepper__line--done" : "")
                  }
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
};

export default CheckoutSteps;
